import { useEffect, useState } from 'react';
import { api } from '../api.js';
import Ticker from '../components/Ticker.jsx';
import HomeBoard from '../components/HomeBoard.jsx';
import CalendarWidget from '../components/widgets/CalendarWidget.jsx';
import OpportunitiesWidget from '../components/widgets/OpportunitiesWidget.jsx';
import ScheduleWidget from '../components/widgets/ScheduleWidget.jsx';
import ScheduledJobsWidget from '../components/widgets/ScheduledJobsWidget.jsx';
import AnalyticsWidget from '../components/widgets/AnalyticsWidget.jsx';
import SalesTodoWidget from '../components/widgets/SalesTodoWidget.jsx';
import JobMapWidget from '../components/widgets/JobMapWidget.jsx';
import CamerasWidget from '../components/widgets/CamerasWidget.jsx';
import PhoneLiveWidget from '../components/widgets/PhoneLiveWidget.jsx';
import SkeletonJobsBanner from '../components/SkeletonJobsBanner.jsx';
import { Header, Page, CollapsibleSection } from '../components.jsx';

// section id -> default *collapsed* state. Absence of a server-side row for
// a given section (new user, or a section id introduced after they last
// touched their prefs) falls back to this.
const SECTION_DEFAULTS = {
  apps: false,
  'sales-todo': false,
  'scheduled-jobs': false,
  'business-overview': true,
  phone: true,
  cameras: true,
  analytics: true,
};

// The header's jump-to menu, in page order. `id` is both the section's
// collapsed-state key and (prefixed) its DOM id.
const NAV = [
  { id: 'apps', label: 'Apps' },
  { id: 'sales-todo', label: 'Sales to-do' },
  { id: 'scheduled-jobs', label: 'Scheduled jobs' },
  { id: 'business-overview', label: 'Overview' },
  { id: 'phone', label: 'Phone' },
  { id: 'cameras', label: 'Cameras' },
  { id: 'analytics', label: 'Analytics' },
];
const domId = (id) => `section-${id}`;

export default function HomePage({ user, editMode, onToggleEdit }) {
  // Editing is desktop-only -- md:HomeBoard already renders read-only cards
  // when editMode is false, and the "Edit layout" toggle itself is hidden
  // below md in the Header, so mobile never reaches an edit state.
  const [collapsed, setCollapsed] = useState(SECTION_DEFAULTS);

  useEffect(() => {
    api
      .get('/api/widgets/section-prefs')
      .then((serverPrefs) => setCollapsed((d) => ({ ...d, ...serverPrefs })))
      .catch(() => {}); // a failed fetch just leaves the defaults in place, not fatal
  }, []);

  const toggle = (id) => {
    const next = !collapsed[id];
    setCollapsed((c) => ({ ...c, [id]: next })); // optimistic, not rolled back on failure
    api.put(`/api/widgets/section-prefs/${id}`, { collapsed: next }).catch(() => {});
  };

  // Jump to a section, opening it first if it's collapsed. That open is
  // deliberately local only -- not saved like a chevron click -- so jumping to
  // Cameras for a look doesn't permanently re-open something you keep shut.
  const jumpTo = (id) => {
    setCollapsed((c) => (c[id] ? { ...c, [id]: false } : c));
    // The target's own top edge doesn't move when its content expands, so
    // there's nothing to wait for before scrolling.
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(domId(id))?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  // A user who's collapsed "apps" and then clicks "Edit layout" shouldn't
  // land on an empty section -- can't usefully edit a board you can't see.
  const appsOpen = editMode || !collapsed.apps;

  return (
    <>
      <Header user={user} editMode={editMode} onToggleEdit={onToggleEdit} nav={NAV} onNavigate={jumpTo} />
      <Ticker />
      <Page wide>
        <SkeletonJobsBanner />
        <div id={domId('apps')} className="mb-4 flex scroll-mt-24 items-center justify-between gap-2">
          <h1 className="text-xl font-bold">{user.name.split(' ')[0]}'s apps</h1>
          {!editMode && (
            <button onClick={() => toggle('apps')} className="text-xs text-steel underline">
              {collapsed.apps ? 'Show' : 'Hide'}
            </button>
          )}
        </div>
        {/* Always mounted, hidden via CSS only -- unlike CollapsibleSection's
            lazy-mount-on-first-open, unmounting/remounting HomeBoard on every
            toggle would lose in-progress drag-and-drop edit state. */}
        <div className={appsOpen ? '' : 'hidden'}>
          <HomeBoard apiBase="/api/home" loadUrl="/api/home" editMode={editMode} />
        </div>

        <CollapsibleSection id={domId('sales-todo')} title="Sales to-do" open={!collapsed['sales-todo']} onToggle={() => toggle('sales-todo')}>
          <SalesTodoWidget />
          <JobMapWidget />
        </CollapsibleSection>

        <CollapsibleSection id={domId('scheduled-jobs')} title="Scheduled jobs this week" open={!collapsed['scheduled-jobs']} onToggle={() => toggle('scheduled-jobs')}>
          <ScheduledJobsWidget />
        </CollapsibleSection>

        <CollapsibleSection id={domId('business-overview')} title="Business overview" open={!collapsed['business-overview']} onToggle={() => toggle('business-overview')}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <OpportunitiesWidget />
            <CalendarWidget />
            <ScheduleWidget />
          </div>
        </CollapsibleSection>

        <CollapsibleSection id={domId('phone')} title="On the phone" open={!collapsed.phone} onToggle={() => toggle('phone')}>
          <PhoneLiveWidget />
        </CollapsibleSection>

        <CollapsibleSection id={domId('cameras')} title="Cameras" open={!collapsed.cameras} onToggle={() => toggle('cameras')}>
          <CamerasWidget />
        </CollapsibleSection>

        <CollapsibleSection id={domId('analytics')} title="Website analytics" open={!collapsed.analytics} onToggle={() => toggle('analytics')}>
          <AnalyticsWidget />
        </CollapsibleSection>
      </Page>
    </>
  );
}
